import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import avalonRevival from "../assets/Carousel_Pics_20260518/AvalonRevival_June13.jpg";
// import billyWalsh from "../assets/Carousel_Pics_20260518/BillyWalsh_August22.jpg";
import cynthiaRenee from "../assets/Carousel_Pics_20260518/CynthiaRenee_June20.jpg";
import donnyTheWaywards from "../assets/Carousel_Pics_20260518/DonnyTheWaywards_June27.jpg";
import dunsbarRoad from "../assets/Carousel_Pics_20260518/DunsbarRoad_August8.jpg";
import geoffreyMiller from "../assets/Carousel_Pics_20260518/GeoffreyMiller_August1.jpg";
import gregSutton from "../assets/Carousel_Pics_20260518/GregSuttonFriends_Aug29.jpg";
import groovyJudy from "../assets/Carousel_Pics_20260518/GroovyJudy_August15.jpg";
import nathanIgnacio from "../assets/Carousel_Pics_20260518/NathanIgnacio_June16.png";
import network from "../assets/Carousel_Pics_20260518/Network_July25.jpg";
// import sugarHigh from "../assets/Carousel_Pics_20260518/SugarHigh_August8.jpg";
import theFabulousOffBrothers from "../assets/Carousel_Pics_20260518/TheFabulousOffBrothers_Sept12.jpg";
import theSkyKings from "../assets/Carousel_Pics_20260518/TheSkyKings_September5.jpg";
import tomRigney from "../assets/Carousel_Pics_20260518/TomRigney_July18.jpg";

const flyers = [
  { src: avalonRevival, alt: "Avalon Revival event flyer", date: "2026-06-13" },
  // { src: billyWalsh, alt: "Billy Walsh event flyer", date: "2026-08-22" },
  { src: cynthiaRenee, alt: "Cynthia Renee event flyer", date: "2026-06-20" },
  {
    src: donnyTheWaywards,
    alt: "Donny & The Waywards event flyer",
    date: "2026-06-27",
  },
  { src: dunsbarRoad, alt: "Dunsbar Road event flyer", date: "2026-08-09" },
  {
    src: geoffreyMiller,
    alt: "Geoffrey Miller event flyer",
    date: "2026-08-01",
  },
  { src: groovyJudy, alt: "Groovy Judy event flyer", date: "2026-08-15" },
  {
    src: gregSutton,
    alt: "Greg Sutton & Friends event flyer",
    date: "2026-08-29",
  },
  {
    src: nathanIgnacio,
    alt: "Nathan Ignacio event flyer",
    date: "2026-06-16",
    // Portrait flyer (2:3) among landscape ones — contain so the full poster,
    // including the name/venue text, stays visible instead of being cropped.
    fit: "contain",
    // Always lead the carousel regardless of the shuffle.
    pinFirst: true,
  },
  { src: network, alt: "Network event flyer", date: "2026-07-25" },
  // { src: sugarHigh, alt: "Sugar High event flyer", date: "2026-08-08" },
  {
    src: theFabulousOffBrothers,
    alt: "The Fabulous Off Brothers event flyer",
    date: "2026-09-12",
  },
  { src: theSkyKings, alt: "The Sky Kings event flyer", date: "2026-09-05" },
  { src: tomRigney, alt: "Tom Rigney event flyer", date: "2026-07-18" },
];

const TRANSITION_MS = 300;

const FlyerCarousel = ({ interval = 5000 }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const shuffledFlyers = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcoming = flyers.filter((f) => {
      // Parse "YYYY-MM-DD" as a local date; `new Date("YYYY-MM-DD")` parses as
      // UTC midnight, which is the prior day in western timezones and would
      // wrongly filter out an event happening today.
      const [y, m, d] = f.date.split("-").map(Number);
      return new Date(y, m - 1, d) >= today;
    });
    for (let i = upcoming.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [upcoming[i], upcoming[j]] = [upcoming[j], upcoming[i]];
    }
    // Keep pinned flyers (e.g. Nathan) at the front so they always show first.
    const pinned = upcoming.filter((f) => f.pinFirst);
    const rest = upcoming.filter((f) => !f.pinFirst);
    return [...pinned, ...rest];
  }, []);

  const total = shuffledFlyers.length;

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev === total - 1 ? 0 : prev + 1));
  }, [total]);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? total - 1 : prev - 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const handleCenterClick = () => {
    navigate("/events");
  };

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(nextSlide, interval);
    return () => clearInterval(timer);
  }, [isPaused, nextSlide, interval]);

  const getOffset = (index) => {
    let offset = index - currentIndex;
    if (offset > total / 2) offset -= total;
    if (offset < -total / 2) offset += total;
    return offset;
  };

  // Side cards use calc() so the gap from the center card is always a fixed 24px,
  // regardless of viewport width — no overlap at any screen size.
  // Gap math: half center (375) + 24px gap + half scaled side (750*0.72/2=270) = 669px.
  const getDesktopSlideStyle = (offset) => {
    const transition = `left ${TRANSITION_MS}ms ease-in-out, transform ${TRANSITION_MS}ms ease-in-out, opacity ${TRANSITION_MS}ms ease-in-out`;
    if (offset === 0) {
      return {
        left: "50%",
        transform: "translateX(-50%)",
        opacity: 1,
        zIndex: 10,
        transition,
      };
    }
    if (offset === -1) {
      return {
        left: "calc(50% - 685px)",
        transform: "translateX(-50%) scale(0.72)",
        opacity: 0.4,
        zIndex: 5,
        transition,
      };
    }
    if (offset === 1) {
      return {
        left: "calc(50% + 685px)",
        transform: "translateX(-50%) scale(0.72)",
        opacity: 0.4,
        zIndex: 5,
        transition,
      };
    }
    if (offset < 0) {
      return {
        left: "calc(50% - 1400px)",
        transform: "translateX(-50%) scale(0.5)",
        opacity: 0,
        zIndex: 1,
        transition,
      };
    }
    return {
      left: "calc(50% + 1400px)",
      transform: "translateX(-50%) scale(0.5)",
      opacity: 0,
      zIndex: 1,
      transition,
    };
  };

  if (total === 0) return null;

  return (
    <div
      className="relative w-full bg-[#6B492A]"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="relative w-full overflow-hidden aspect-[4/3] md:aspect-auto md:h-[600px]">
        {/* Mobile: full-width track slider, one image at a time */}
        <div className="md:hidden absolute inset-0">
          <div
            className="flex h-full"
            style={{
              transform: `translateX(-${(currentIndex * 100) / total}%)`,
              transition: `transform ${TRANSITION_MS}ms ease-in-out`,
              width: `${total * 100}%`,
            }}
          >
            {shuffledFlyers.map((flyer, index) => (
              <div
                key={flyer.src}
                className="h-full flex items-center justify-center"
                style={{ width: `${100 / total}%` }}
                onClick={index === currentIndex ? handleCenterClick : undefined}
              >
                <img
                  src={flyer.src}
                  alt={flyer.alt}
                  className={`w-full h-full ${
                    flyer.fit === "contain"
                      ? "object-contain bg-black"
                      : "object-cover"
                  }`}
                  loading={index === 0 ? "eager" : "lazy"}
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Desktop: center card + peeking neighbors */}
        <div className="hidden md:block absolute inset-0">
          {shuffledFlyers.map((flyer, index) => {
            const offset = getOffset(index);
            const isCenter = offset === 0;
            return (
              <div
                key={flyer.src}
                className={`absolute top-0 h-full ${isCenter ? "cursor-pointer" : ""}`}
                style={{ ...getDesktopSlideStyle(offset), aspectRatio: "5/4" }}
                aria-hidden={!isCenter}
                onClick={isCenter ? handleCenterClick : undefined}
                role={isCenter ? "link" : undefined}
              >
                <img
                  src={flyer.src}
                  alt={flyer.alt}
                  className={`w-full h-full ${
                    flyer.fit === "contain"
                      ? "object-contain bg-black"
                      : "object-cover"
                  }`}
                  loading={Math.abs(offset) <= 1 ? "eager" : "lazy"}
                  draggable={false}
                />
              </div>
            );
          })}
        </div>

        {/* Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-2 md:p-3 rounded-full transition-colors"
          aria-label="Previous flyer"
        >
          <svg
            className="w-5 h-5 md:w-6 md:h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-2 md:p-3 rounded-full transition-colors"
          aria-label="Next flyer"
        >
          <svg
            className="w-5 h-5 md:w-6 md:h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        {/* Dots */}
        <div className="absolute bottom-3 md:bottom-5 left-1/2 -translate-x-1/2 z-20 flex justify-center gap-1.5 md:gap-2 flex-nowrap max-w-[95%]">
          {shuffledFlyers.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-1.5 md:h-2 rounded-full transition-all flex-shrink-0 ${
                index === currentIndex
                  ? "w-5 md:w-8 bg-white"
                  : "w-1.5 md:w-2 bg-white/60 hover:bg-white/80"
              }`}
              aria-label={`Go to flyer ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FlyerCarousel;
